import { IMAGES } from "@/lib/products";

const Box = ({ className = "", children }) => (
  <div className={`border border-gold/70 rounded-md ${className}`}>{children}</div>
);

const Label = ({ children, className = "" }) => (
  <div className={`text-[7px] md:text-[8px] uppercase tracking-[0.18em] text-gold font-semibold ${className}`}>
    {children}
  </div>
);

const Fill = ({ className = "" }) => (
  <div className={`rounded-sm bg-ivory ${className}`} />
);

function MonthlyDashboard() {
  return (
    <div className="flex h-full flex-col items-center justify-center p-5 text-center">
      <Label>The Monthly System</Label>
      <div className="mt-5 font-serif text-2xl md:text-3xl font-bold leading-none text-espresso">
        Monthly <span className="italic text-gold">Dashboard</span>
      </div>
      <div className="mt-7 flex h-12 w-12 items-center justify-center rounded-full border border-gold font-serif italic text-gold">
        01
      </div>
      <p className="mt-6 max-w-[210px] font-serif italic text-sm md:text-base font-semibold leading-snug text-espresso">
        Six months to plan with intention, notice your patterns, and build momentum that lasts.
      </p>
      <div className="mt-7 grid w-full grid-cols-2 gap-2 text-[7px] uppercase tracking-[0.14em] text-espresso">
        {["6 Monthly Overviews", "Goals & Priorities", "Habit and Mood Systems", "Life Audit Reflections"].map((item) => (
          <Box key={item} className="px-2 py-2">{item}</Box>
        ))}
      </div>
    </div>
  );
}

function DailyDashboard() {
  return (
    <div className="h-full p-4">
      <Label>Daily</Label>
      <div className="mt-2 font-serif text-2xl md:text-3xl font-bold text-espresso">
        Daily Dashboard <span className="italic text-gold">Day 1</span>
      </div>
      <div className="mt-5 grid h-[78%] grid-cols-[1.1fr_1.1fr_1.4fr] gap-2">
        <div className="space-y-2">
          <Label>+ Today&apos;s Main Mission</Label>
          <Box className="h-[35%]" />
          <Box className="h-[28%]" />
          <Box className="h-[28%]" />
        </div>
        <div className="space-y-2">
          <Box className="h-[25%] p-3">
            <Label>+ Top 3 Must-Do</Label>
            <div className="mt-5 grid grid-cols-2 gap-3 text-[8px] text-espresso">
              <span>□ 1</span><span>□ 2</span><span>□ 3</span>
            </div>
          </Box>
          <Box className="h-[35%]" />
          <Box className="h-[31%]" />
        </div>
        <div className="space-y-2">
          <Label>+ Date · Mood · Energy</Label>
          <div className="mt-2 flex gap-4 text-sm text-espresso">☺ ○ ◡ ⌒</div>
          <Box className="h-[18%] p-3">
            <Label>+ Water</Label>
            <div className="mt-2 flex flex-wrap gap-1">
              {Array.from({ length: 8 }).map((_, i) => <span key={i} className="h-2 w-2 rounded-full border border-gold bg-espresso/80" />)}
            </div>
          </Box>
          <Box className="h-[20%] p-3"><Label>+ Meals · Movement</Label></Box>
          <Box className="h-[22%] p-3"><Label>+ Money · Screen Time</Label></Box>
          <Box className="h-[16%] p-3"><Label>+ Today&apos;s Win</Label></Box>
        </div>
      </div>
    </div>
  );
}

function WeeklyReset() {
  return (
    <div className="h-full p-5">
      <Label>Reflect & Reset</Label>
      <div className="mt-2 font-serif text-3xl font-bold text-espresso">Weekly Reset</div>
      <div className="mt-8 grid h-[78%] grid-cols-2 gap-3">
        <div className="space-y-4">
          <Label>What Happened This Week</Label>
          <Box className="h-[36%]" />
          <Box className="h-[34%] p-3"><Label>One Promise For Next Week</Label></Box>
        </div>
        <div className="space-y-4">
          <Box className="h-[34%] p-3"><Label>What Went Well</Label></Box>
          <Label>What Needs To Change</Label>
          <Box className="h-[34%] p-3"><Label>Your Unfiltered Thoughts</Label></Box>
        </div>
      </div>
    </div>
  );
}

function TriggerTracker() {
  return (
    <div className="h-full p-5">
      <div className="font-serif text-3xl font-bold text-espresso">Trigger Tracker</div>
      <div className="mt-8 grid h-[82%] grid-cols-2 gap-3">
        <div className="space-y-4">
          <Label>What Triggered Me</Label>
          <Box className="h-[36%] p-3"><Label>How Did I React</Label></Box>
          <Box className="h-[30%] p-3"><Label>Support I Need</Label></Box>
        </div>
        <div className="space-y-4">
          <Box className="h-[32%] p-3"><Label>What Emotion Came Up</Label></Box>
          <Label>What Could I Do Better</Label>
          <Box className="h-[30%] p-3"><Label>Your Unfiltered Thoughts</Label></Box>
        </div>
      </div>
    </div>
  );
}

function SpendingTracker() {
  return (
    <div className="h-full p-4">
      <Label>Money Control</Label>
      <div className="mt-2 font-serif text-3xl font-bold text-espresso">Daily Spending Tracker</div>
      <div className="mt-8 overflow-hidden rounded-md border border-gold/70">
        <div className="grid grid-cols-6 bg-ivory text-center text-[7px] uppercase tracking-[0.1em] text-gold font-semibold">
          {["Date", "Item", "Need / Want", "Amount", "Payment", "Worth It?"].map((h) => (
            <div key={h} className="border-r border-gold/70 py-2 last:border-r-0">{h}</div>
          ))}
        </div>
        {Array.from({ length: 7 }).map((_, row) => (
          <div key={row} className="grid grid-cols-6">
            {Array.from({ length: 6 }).map((__, col) => (
              <Fill key={col} className="h-12 border-r border-t border-gold/70 last:border-r-0" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlannerGallery() {
  const images = [IMAGES.planner_closed, IMAGES.planner_open, IMAGES.planner_page, IMAGES.planner_flatlay];

  return (
    <div className="grid h-full grid-cols-2 gap-2 p-3">
      {images.map((src) => (
        <img key={src} src={src} alt="" className="h-full w-full rounded-md object-cover" loading="lazy" />
      ))}
    </div>
  );
}

const PREVIEWS = {
  monthly: MonthlyDashboard,
  daily: DailyDashboard,
  weekly: WeeklyReset,
  trigger: TriggerTracker,
  spending: SpendingTracker,
  gallery: PlannerGallery,
};

export function PlannerPagePreview({ page, className = "" }) {
  if (page.img?.startsWith("/planner-pages/")) {
    return (
      <img
        src={page.img}
        alt={page.label}
        className={`h-full w-full bg-cream object-cover object-top ${className}`}
        loading="lazy"
      />
    );
  }

  const Preview = PREVIEWS[page.key] || PlannerGallery;

  return (
    <div className={`h-full w-full overflow-hidden bg-cream text-espresso ${className}`}>
      <Preview />
    </div>
  );
}
